import {Dealership} from '@nxcr-org/web-api-interface/lib/dealer_pb'
import {Address} from '@nxcr-org/web-api-interface/lib/domain_pb'
import {
  AddVehicleRequest,
  AddVehiclesRequest,
  DeleteMilesPurchasedLogRequest, DeleteVehicleMediaRequest,
  GetSubscriptionMileagePurchasesRequest,
  GetSubscriptionMileagePurchasesResponse,
  GetSubscriptionMileageRequest,
  GetSubscriptionMileageResponse,
  GetVehicleMediaRequest,
  InitiateVehicleReturnRequest,
  ListVehicleMakesRequest,
  LogMilesPurchasedRequest,
  ReturnDetails,
  ReturnVehicleRequest,
  UpdateVehicleRequest,
  UpsertVehicleMediaRequest
} from '@nxcr-org/web-api-interface/lib/fleet_service_pb'
import * as FleetServicePb from '@nxcr-org/web-api-interface/lib/fleet_service_pb'
import {Odometer, Vehicle} from '@nxcr-org/web-api-interface/lib/vehicle_pb'
import {NexusServicePromiseClient} from '@nxcr-org/web-api-interface/lib/nexus_service_grpc_web_pb'
import {
  GetLatestCustodyRequest,
} from '@nxcr-org/web-api-interface/lib/nexus_service_pb'
import {oktaAuth, OktaAuthInterceptor} from '../../okta/config'
import {spyOnError} from '../../utils/spyOnResponse'
import {Env} from '../fleet-management/api/env'
import {FleetServicePromiseClient} from '@nxcr-org/web-api-interface/lib/gateway_service_grpc_web_pb'
import {tryParseInt} from '../../utils/utils'
import Units = Odometer.Units

function getNexusService(): NexusServicePromiseClient {
  const idToken = oktaAuth.getIdToken()
  const target = Env.endpoints.fleet

  return new NexusServicePromiseClient(target, null, {
    unaryInterceptors: [new OktaAuthInterceptor(idToken)],
    'grpc-node.max_session_memory': 1000,
    'grpc-node.max_send_message_length': 1024 * 1024 * 100,
    'grpc-node.max_receive_message_length': 1024 * 1024 * 100,
  })
}
function getFleetService(): FleetServicePromiseClient {
  const idToken = oktaAuth.getIdToken()
  const target = Env.endpoints.fleet

  return new FleetServicePromiseClient(target, null, {
    unaryInterceptors: [new OktaAuthInterceptor(idToken)],
    'grpc-node.max_session_memory': 1000,
    'grpc-node.max_send_message_length': 1024 * 1024 * 100,
    'grpc-node.max_receive_message_length': 1024 * 1024 * 100,
  })
}
export function buildGetLatestCustodyRequest(
  params: Pick<GetLatestCustodyRequest.AsObject, 'vehicleId'>
) {
  const request = new GetLatestCustodyRequest()

  request.setCustodyType(GetLatestCustodyRequest.CustodyType.ANY)
  request.setVehicleId(params.vehicleId)

  return request
}

export async function getLatestCustody(
  params: Pick<GetLatestCustodyRequest.AsObject, 'vehicleId'>
) {
  const client = getNexusService()
  const request = buildGetLatestCustodyRequest(params)

  return client
    .getLatestCustody(request)
    .then((res) => {
      return res.toObject()
    })
    .catch(spyOnError('getLatestCustody'))
    .catch(() => {
      return {
        dealership: {
          name: 'Not Available',
          address: {
            street: 'Not Available',
          } as Address.AsObject,
        } as Dealership.AsObject,
      }
    })
}

export async function getSubscriptionMileage(
  subscriptionId: string
): Promise<GetSubscriptionMileageResponse.AsObject> {
  const client = getNexusService()
  const request = new GetSubscriptionMileageRequest()
  request.setSubscriptionId(subscriptionId)

  return client.getSubscriptionMileage(request).then((res) => {
    return res.toObject()
  })
}

export async function getSubscriptionMileagePurchases(
  subscriptionId: string
): Promise<GetSubscriptionMileagePurchasesResponse.AsObject> {
  const client = getNexusService()
  const request = new GetSubscriptionMileagePurchasesRequest()
  request.setSubscriptionId(subscriptionId)

  return client.getSubscriptionMileagePurchases(request).then((res) => {
    return res.toObject()
  })
}

export async function LogMilesPurchased(
  params: LogMilesPurchasedRequest.AsObject
) {
  const client = getFleetService()
  const request = new LogMilesPurchasedRequest()
  request.setVehicleId(params.vehicleId)
  request.setSubscriptionId(params.subscriptionId)
  request.setAmount(params.amount)
  request.setPrice(params.price)
  request.setTime(params.time)
  request.setReferenceId(params.referenceId)

  return client.logMilesPurchased(request).then((res) => {
    return res.toObject()
  })
}

export async function deleteMilesPurchasedLog(id: number) {
  const client = getFleetService()
  const request = new DeleteMilesPurchasedLogRequest()
  request.setId(id)

  return client.deleteMilesPurchasedLog(request).then((res) => {
    return res.toObject()
  })
}

export async function listVehicleMakes() {
  const client = getNexusService()
  const request = new ListVehicleMakesRequest()

  return client.listVehicleMakes(request).then((res) => {
    return res.toObject()?.makesList || []
  })
}

export async function listModelsByMake(make: string) {
  const client = getNexusService() as any
  const ListModelsByMakeRequest = (FleetServicePb as any).ListModelsByMakeRequest
  const request = ListModelsByMakeRequest ? new ListModelsByMakeRequest() : new ListVehicleMakesRequest()

  if (typeof request.setMake === 'function') {
    request.setMake(make)
  }

  const requestFn = client.listModelsByMake || client.ListModelsByMake
  if (typeof requestFn !== 'function') {
    return []
  }

  return requestFn.call(client, request).then((res) => {
    const objectResponse = res?.toObject?.() || {}
    return objectResponse?.modelsList || []
  })
}

export async function updateVehicle(params: {
  vehicle: Vehicle
}) {
  const client = getNexusService()
  const request = new UpdateVehicleRequest()
  request.setId(params.vehicle.getId())
  request.setVehicle(params.vehicle)

  return client.updateVehicle(request).then((res) => {
    return res.toObject()
  })
}

export async function addVehicle(params: {
  programId: string
  financier: string
  vehicleJson: vehicleJson
}) {
  const client = getNexusService()
  const request = new AddVehicleRequest()

  request.setProgramId(params.programId)
  request.setFinancier(params.financier)
  request.setVehicleJson(JSON.stringify(params.vehicleJson))

  return client.addVehicle(request).then((res) => {
    return res.toObject()
  })
}

export async function addVehicles(params: {
  programId: string
  financier: string
  vehicleJsons: vehicleJson[]
}) {
  const client = getNexusService()
  const request = new AddVehiclesRequest()

  request.setProgramId(params.programId)
  request.setFinancier(params.financier)

  // Convert each vehicle JSON to string and add to the request
  const vehicleJsonStringsList = params.vehicleJsons.map(vehicleJson =>
    JSON.stringify(vehicleJson)
  )
  request.setVehicleJsonsList(vehicleJsonStringsList)

  return client.addVehicles(request).then((res) => {
    return res.toObject()
  })
}

export async function returnVehicle(subscriptionId: string, vehicleId: string) {
  const client = getNexusService()
  const request = new ReturnVehicleRequest()
  request.setSubscriptionId(subscriptionId)
  const rd = new ReturnDetails()
  rd.setVehicleId(vehicleId)
  rd.setOdometer(10)
  rd.setTime(new Date().toISOString())
  rd.setInspectionReportId(1)
  request.setReturnDetails(rd)

  return client.returnVehicle(request).then((res) => {
    return res.toObject()
  })
}

type vehicleJson = {
  vin: string
  make: string
  model: string
  trim: string
  year: number
  exteriorColor: string
  interiorColor: string
  startingOdometerMiles: number
  additionalDetails: string
}

export async function getVehicleMedia( clientId: string, make: string, model: string, year: string, trim: string) {
  const client = getNexusService()
  const request = new GetVehicleMediaRequest()
  request.setMake(make)
  request.setModel(model)
  request.setYear(tryParseInt(year, 10, '0'))
  request.setTrim(trim)
  const metadata = { 'client-id': clientId }

  return client.getVehicleMedia(request, metadata).then((res) => {
    return res.toObject()
  })
}

export async function upsertVehicleMedia(params: {
  clientId: string
  upsertVehicleMediaRequest: UpsertVehicleMediaRequest
}) {
  const client = getNexusService()
  const metadata = { 'client-id': params.clientId }
  return client.upsertVehicleMedia(params.upsertVehicleMediaRequest, metadata).then((res) => {
    return res.toObject()
  })
}

export async function deleteVehicleMedia(vehicleMediaId: string) {
  const client = getNexusService()
  const request = new DeleteVehicleMediaRequest()
  request.setId(vehicleMediaId)
  return client.deleteVehicleMedia(request).then((res) => {
    return res.toObject()
  })
}

export async function initiateVehicleReturn(
  params: InitiateVehicleReturnRequest.AsObject
) {
  const client = getNexusService()
  const request = new InitiateVehicleReturnRequest()
  const odometer = new Odometer()
  odometer.setUnits(params.odometer?.units || Units.MILES)
  odometer.setReading(params.odometer?.reading || 0)
  odometer.setReadingType(params.odometer?.readingType || Odometer.ReadingType.DROPOFF)
  odometer.setDate(params.odometer?.date || Date.now())
  request.setVehicleId(params.vehicleId)
  request.setVin(params.vin)
  request.setCustomerId(params.customerId)
  request.setOdometer(odometer)
  request.setSubscriptionId(params.subscriptionId)
  request.setReturnTime(params.returnTime || new Date().toISOString())
  
  if (params.missingAssetsAmount) {
    request.setMissingAssetsAmount(params.missingAssetsAmount)
  }
  if (params.initialDamagesAmount) {
    request.setInitialDamagesAmount(params.initialDamagesAmount)
  }

  return client.initiateVehicleReturn(request).then((res) => {
    return res.toObject()
  })
}
